import { storeToRefs } from 'pinia';
import { useMeStore } from '@/services/UserService/useMeStore';
import { useCompanyStore } from '@/services/UserService/useCompanyStore';
import { useInvoiceState } from '@/modules/billing/review/editDraft/useInvoiceState';
export function useInvoiceGroups() {
  const {
    features
  } = storeToRefs(useMeStore());
  const {
    companySettings
  } = storeToRefs(useCompanyStore());
  const invoiceState = useInvoiceState();
  const sectionOrder = ['Fixed Fee', 'Fixed Fee Detail', 'Services', 'Expenses', 'Services (Not Billed)'];
  function toggleGroup(group) {
    group.hide = !group.hide;
  }
  function hasCredit(group) {
    return group.lineItems?.some(lineItem => lineItem?.credit === true);
  }
  function hasItemSelected(group) {
    return group.lineItems?.some(lineItem => lineItem?.checked === true);
  }
  function showAddAndUpdateMenus(group) {
    if (!group || !group.lineItems) return false;
    let show = ['billableEntries', 'nonBillableEntries', 'combinedEntries', 'fixedFeeEntries', 'fixedFees', 'expenses'].includes(group.type);
    return show;
  }
  function isEntryGroup(group) {
    return group.type?.indexOf('ntries') > -1;
  }
  function selectGroupType(group) {
    if (group.type.indexOf('ntries') > -1) {
      return 'Entry';
    } else if (group.type === 'expenses') {
      return 'Expense';
    } else if (group.type === 'fixedFees') {
      return 'FixedFee';
    }
    return group.type || 'Other line item type';
  }
  const extractSectionType = groupName => {
    let match = groupName.match(/\((.*?)\)$/);
    return match ? match[1].trim() : 'Unknown';
  };
  const roundedTime = (t, defaultHourValue) => {
    t = new Date(t);
    t.setMinutes(Math.floor(t.getMinutes() / (60 * defaultHourValue)) * (60 * defaultHourValue));
    t.setSeconds(0);
    return t;
  };
  const appliesToNonBillable = group => {
    return group.lineItems.some(lineItem => lineItem.entries?.some(entry => entry.billingType === 'nonBillable'));
  };
  const appliesToBillable = group => {
    return group.lineItems.some(lineItem => lineItem.entries?.some(entry => entry.billingType === 'billable'));
  };
  const appliesToFixedFeeEntries = group => {
    return group.lineItems.some(lineItem => lineItem.entries?.some(entry => entry.billingType === 'fixedFee'));
  };
  const appliesToAll = () => true;
  const updateMenuItems = [{
    title: 'Make non-billable',
    action: 'makeNonBillable',
    appliesTo: group => appliesToBillable(group) || appliesToFixedFeeEntries(group)
  }, {
    title: 'Save for later',
    action: 'saveForLater',
    appliesTo: appliesToAll
  }, {
    title: 'Change Matter',
    action: 'changeMatter',
    appliesTo: appliesToAll
  }, {
    title: 'Make billable',
    action: 'makeBillable',
    appliesTo: group => appliesToNonBillable(group) || appliesToFixedFeeEntries(group)
  }, {
    title: 'Assign to Fixed Fee',
    action: 'assignToFixedFee',
    appliesTo: group => {
      // Check if there are any valid fixed fees to assign to
      const fixedFeeGroups = invoiceState.groups.filter(g => g.type === 'fixedFees' || g.name === 'Fixed Fee' || g.lineItems && g.lineItems.some(item => item.charges?.length > 0));
      const hasValidFixedFees = fixedFeeGroups.length > 0;

      // Check if this group has billable or non-billable entries that can be assigned
      const hasAssignableEntries = appliesToBillable(group) || appliesToNonBillable(group);
      return hasValidFixedFees && hasAssignableEntries;
    },
    visible: features.value.editFixedFeeAndExpenseInDraft
  }, {
    title: 'Write-down to zero',
    action: 'writeDownToZero',
    appliesTo: group => appliesToBillable(group),
    visible: companySettings.value.adjustments
  }, {
    title: 'Mark as billed',
    action: 'markedAsBilled',
    appliesTo: group => appliesToBillable(group) || appliesToNonBillable(group) || appliesToFixedFeeEntries(group)
  }];
  return {
    toggleGroup,
    hasCredit,
    hasItemSelected,
    showAddAndUpdateMenus,
    isEntryGroup,
    selectGroupType,
    sectionOrder,
    updateMenuItems,
    extractSectionType,
    roundedTime
  };
}